"use client";

/**
 * PlatformPillarGrid — the four pillars of the platform.
 *
 * Lives on /platform only. Each pillar is a tilted card with a glow,
 * a small chip cloud underneath that previews the contents (agent
 * names, workspace names, provider category names, integration
 * surface names), and a link to the deep page.
 *
 * Visual idiom borrowed from the agent-platform genre but rebuilt in
 * paper-warm + brand red, no vendor names anywhere.
 */

import Link from "next/link";
import { useState } from "react";
import { ArrowUpRight, Bot, Layers, Brain, Plug } from "lucide-react";
import { GlowCard, Tilt, ScrollReveal } from "./animations";
import { FEATURED_AGENTS, countWord } from "@/lib/agent-roster";
import { CanvasRevealEffect } from "./aceternity/canvas-reveal-effect";
import { APPS } from "@/lib/apps-registry";

type Pillar = {
  icon: typeof Bot;
  kicker: string;
  title: string;
  body: string;
  href: string;
  chips: string[];
  cta: string;
};

const PILLARS: Pillar[] = [
  {
    icon: Bot,
    kicker: "Pillar 01",
    title: "Agents",
    body:
      `${countWord(FEATURED_AGENTS.length)} specialists, each fluent in one shape of work. They route to each other, hand off without losing the thread, and ask before doing anything that costs you something.`,
    href: "/agents",
    chips: [
      "Researcher",
      "Coder",
      "Writer",
      "Analyst",
      "Summariser",
      "Browser",
      "Mailer",
      "Trainer",
      "Workflow",
      "Search",
      "Socials",
      "Finance",
    ],
    cta: "Meet the roster",
  },
  {
    icon: Layers,
    kicker: "Pillar 02",
    title: "Workspaces",
    body:
      "Different rooms for different work. One login, one memory of you, one way of moving between them. Chat, research, code, news, the long tail of the verticals — all rendered in the same hand.",
    href: "/apps",
    chips: [
      "Chat",
      "Research",
      "Code",
      "News",
      "Search",
      "Storage",
      "Train",
      "Workflow",
      "Mail & Calendar",
      "Projects",
      "Access",
      "Finance",
      "Data",
      "Med",
      "Edu",
      "Travel",
      "Estate",
      "Socials",
      "Dash",
    ],
    cta: `See all ${APPS.length} workspaces`,
  },
  {
    icon: Brain,
    kicker: "Pillar 03",
    title: "Models",
    body:
      "We pick the right brain for the question, in the open, with one click to override. Reasoning models for hard answers, fast models for small ones, vision for what's on the page, code-tuned for what's on the diff.",
    href: "/models",
    chips: [
      "Reasoning",
      "Chat",
      "Vision",
      "Code-tuned",
      "Long-context",
      "Audio · TTS",
      "Embedding",
      "Image-gen",
      "Open-weight",
    ],
    cta: "How we choose",
  },
  {
    icon: Plug,
    kicker: "Pillar 04",
    title: "Integrations",
    body:
      "Reach the workshop from wherever you happen to be working. A side panel beside what you're reading, a desktop client off the browser tab, a pipeline builder, a public API and tool-call surface for the builders.",
    href: "/api",
    chips: [
      "Browser extension",
      "Desktop client",
      "askFinz OS",
      "Access · identity",
      "Workflow canvas",
      "Public API",
      "Tool-call surface",
      "Webhooks",
      "Model flexibility",
    ],
    cta: "For builders",
  },
];

export function PlatformPillarGrid() {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  return (
    <section className="border-b hairline">
      <div className="container-x py-14 md:py-20">
        <div className="flex items-baseline justify-between border-b hairline pb-4">
          <h2 className="text-sm tracking-[0.18em] uppercase text-foreground/65">
            Four pillars
          </h2>
          <span className="font-mono text-xs text-foreground/65">
            04 · the shape of the platform
          </span>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <ScrollReveal key={p.title} delay={i * 80}>
                <Tilt max={4} className="h-full">
                  <GlowCard
                    glow={300}
                    className="lift-card rounded-2xl border hairline-strong bg-background h-full overflow-hidden"
                  >
                    <div aria-hidden className="absolute inset-0 z-0 pointer-events-none rounded-2xl overflow-hidden">
                      <CanvasRevealEffect active={hoverIdx === i} maxOpacity={0.32} />
                    </div>
                    <Link
                      href={p.href}
                      className="group block p-7 md:p-9 h-full relative z-[1]"
                      onMouseEnter={() => setHoverIdx(i)}
                      onMouseLeave={() => setHoverIdx(null)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border hairline-strong">
                            <Icon className="h-4 w-4" />
                          </span>
                          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-foreground/65">
                            {p.kicker}
                          </span>
                        </div>
                        <ArrowUpRight className="h-4 w-4 text-foreground/65 group-hover:text-foreground transition" />
                      </div>

                      <h3 className="mt-7 display-serif text-3xl md:text-4xl leading-tight">
                        {p.title.split("").map((c, idx) =>
                          idx === 0 ? (
                            <span key={idx} className="text-[hsl(var(--accent))]">
                              {c}
                            </span>
                          ) : (
                            <span key={idx}>{c}</span>
                          )
                        )}
                      </h3>

                      <p className="mt-4 text-foreground/70 leading-relaxed text-[15px]">
                        {p.body}
                      </p>

                      <div className="mt-6 flex flex-wrap gap-1.5">
                        {p.chips.map((chip) => (
                          <span
                            key={chip}
                            className="inline-flex items-center rounded-full border hairline px-2.5 py-0.5 text-[10.5px] font-mono uppercase tracking-[0.12em] text-foreground/65 bg-background/60"
                          >
                            {chip}
                          </span>
                        ))}
                      </div>

                      <div className="mt-7 inline-flex items-center gap-1.5 text-sm text-foreground/75 group-hover:text-foreground transition">
                        {p.cta}
                        <ArrowUpRight className="h-3.5 w-3.5" />
                      </div>
                    </Link>
                  </GlowCard>
                </Tilt>
              </ScrollReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
